/**
 * Brouillon en cours de modification dans le Studio (lot 5) : la copie
 * locale, l'historique (annuler / rétablir), la validation (différée le
 * temps de taper) et l'ENREGISTREMENT AUTOMATIQUE (1,2 s après la dernière
 * modification, revalidé comme une modification à la main). Un brouillon
 * changé ailleurs pendant ce temps (correction par l'IA, autre onglet) est
 * signalé : on recharge, rien n'est écrasé en silence.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { validateJraf, type JrafDocument, type ValidationResult } from '@jeraisonne/animation';
import { checkedDraft, useSaveAnimationDraft, type AnimationRow } from '@/lib/animations/api';

export const AUTOSAVE_MS = 1200;
const VALIDATE_MS = 250;
const HISTORY_MAX = 80;
/** deux modifications du même champ à moins de 1 s : une seule entrée d'historique */
const COALESCE_MS = 1000;

export type SaveState = 'saved' | 'pending' | 'saving' | 'error';

export interface StudioDraft {
  draft: unknown;
  /** validation du brouillon (différée) */
  result: ValidationResult | null;
  /** dernier document VALIDE (l'aperçu le garde pendant une erreur) */
  lastGood: JrafDocument | null;
  edit: (next: (d: unknown) => unknown, coalesceKey?: string) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  save: SaveState;
  saveError: string | null;
  /** le brouillon a changé ailleurs depuis l'ouverture */
  external: boolean;
  reload: () => void;
  /** enregistre tout de suite (avant de quitter, « Publier ») */
  flush: () => void;
}

/**
 * JSON aux clés triées : la base (jsonb) ne garde pas l'ordre des clés, on
 * reconnaît nos propres enregistrements quel que soit cet ordre.
 */
export function canonicalJson(v: unknown): string {
  return JSON.stringify(v, (_k, x: unknown) =>
    x && typeof x === 'object' && !Array.isArray(x)
      ? Object.fromEntries(
          Object.entries(x as Record<string, unknown>).sort(([a], [b]) => (a < b ? -1 : 1)),
        )
      : x,
  );
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function useStudioDraft(
  row: Pick<AnimationRow, 'id' | 'draft'>,
  toolIds: readonly string[] | undefined,
): StudioDraft {
  const [draft, setDraft] = useState<unknown>(row.draft);
  const past = useRef<unknown[]>([]);
  const future = useRef<unknown[]>([]);
  const lastEdit = useRef<{ key: string; at: number } | null>(null);
  const [, bump] = useState(0);
  // `mutate` est stable : l'enregistrement ne change pas d'identité à chaque rendu
  const { mutate } = useSaveAnimationDraft();
  const [save, setSave] = useState<SaveState>('saved');
  const [saveError, setSaveError] = useState<string | null>(null);
  /** ce que la base contient d'après nous (JSON), pour reconnaître nos propres enregistrements */
  const known = useRef(new Set<string>([canonicalJson(row.draft)]));
  const [external, setExternal] = useState(false);

  const edit = useCallback((next: (d: unknown) => unknown, coalesceKey?: string) => {
    setDraft((cur: unknown) => {
      const out = next(cur);
      if (out === cur) return cur;
      const now = Date.now();
      const same =
        coalesceKey &&
        lastEdit.current?.key === coalesceKey &&
        now - lastEdit.current.at < COALESCE_MS;
      if (!same) {
        past.current = [...past.current.slice(-HISTORY_MAX + 1), cur];
        future.current = [];
      }
      lastEdit.current = coalesceKey ? { key: coalesceKey, at: now } : null;
      return out;
    });
    setSave('pending');
  }, []);

  const undo = useCallback(() => {
    setDraft((cur: unknown) => {
      const prev = past.current.at(-1);
      if (prev === undefined) return cur;
      past.current = past.current.slice(0, -1);
      future.current = [cur, ...future.current];
      return prev;
    });
    lastEdit.current = null;
    setSave('pending');
    bump((n) => n + 1);
  }, []);

  const redo = useCallback(() => {
    setDraft((cur: unknown) => {
      const [next, ...rest] = future.current;
      if (next === undefined) return cur;
      future.current = rest;
      past.current = [...past.current, cur];
      return next;
    });
    lastEdit.current = null;
    setSave('pending');
    bump((n) => n + 1);
  }, []);

  // validation différée (la validation complète mesure toute la mise en page)
  const checking = useDebounced(draft, VALIDATE_MS);
  const result = useMemo(
    () => validateJraf(checking, toolIds ? { toolIds } : {}),
    [checking, toolIds],
  );
  // dernier document valide : l'aperçu le garde pendant qu'on corrige une erreur
  const goodRef = useRef<JrafDocument | null>(null);
  if (result.ok) goodRef.current = result.doc;

  // enregistrement automatique
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const doSave = useCallback(() => {
    const d = draftRef.current;
    const payload = checkedDraft(d, toolIds);
    known.current.add(canonicalJson(payload.draft));
    setSave('saving');
    mutate(
      { id: row.id, ...payload },
      {
        onSuccess: () => {
          setSaveError(null);
          // modifié pendant l'enregistrement : il reste à faire
          setSave(draftRef.current === d ? 'saved' : 'pending');
        },
        onError: (e) => {
          setSave('error');
          setSaveError(e.message);
        },
      },
    );
  }, [mutate, row.id, toolIds]);

  useEffect(() => {
    if (save !== 'pending') return;
    const t = setTimeout(doSave, AUTOSAVE_MS);
    return () => clearTimeout(t);
  }, [draft, save, doSave]);

  // brouillon changé ailleurs (correction par l'IA, autre onglet)
  useEffect(() => {
    if (!known.current.has(canonicalJson(row.draft))) setExternal(true);
  }, [row.draft]);

  const reload = useCallback(() => {
    known.current = new Set([canonicalJson(row.draft)]);
    past.current = [];
    future.current = [];
    lastEdit.current = null;
    setDraft(row.draft);
    setExternal(false);
    setSave('saved');
  }, [row.draft]);

  // stable : appelé au départ de la page avec l'état du moment
  const saveRef = useRef(save);
  saveRef.current = save;
  const doSaveRef = useRef(doSave);
  doSaveRef.current = doSave;
  const flush = useCallback(() => {
    if (saveRef.current === 'pending' || saveRef.current === 'error') doSaveRef.current();
  }, []);

  return {
    draft,
    result,
    lastGood: goodRef.current,
    edit,
    undo,
    redo,
    canUndo: past.current.length > 0,
    canRedo: future.current.length > 0,
    save,
    saveError,
    external,
    reload,
    flush,
  };
}
